import type { IdGenerator } from "../../rxjs/configureEpics"
import type { Action } from "../../redux/types"
import { accessLevelCreated, accessLevelUpdated, permissionCreated, permissionDeleted } from "../../data/actions"
import { composePermission } from "./utils"
import { PermissionType } from "./selectors"

type AccessLevelSaveRequestedPayload = {
    accessLevel: {
        id: string
        name: string
    }
    permissions: AccessLevelPermissionPayload[]
}

type AccessLevelPermissionPayload = {
    id: string
    name: string
    accessLevelId: string
}

type RoleFormSubmittedPayload = {
    oldValue?: Record<string, any> | null
    newValue: Record<string, any> & { roleName: string }
}

const getPermissionKeys = (newValue: RoleFormSubmittedPayload["newValue"]): string[] => {
    return Object.keys(newValue).filter(key => key !== "roleName")
}

const buildPermissions = (
    permissionKeys: string[],
    newValue: RoleFormSubmittedPayload["newValue"],
    oldValue: RoleFormSubmittedPayload["oldValue"],
    accessLevelId: string,
    idgen: IdGenerator
): {
    permissions: AccessLevelPermissionPayload[]
    permissionsByKey: Record<string, AccessLevelPermissionPayload>
} => {
    const permissionsByKey: Record<string, AccessLevelPermissionPayload> = {}
    const permissions = permissionKeys
        .filter(key => !!newValue[key])
        .map(key => {
            const existingPermissionId = oldValue?.[key]?.id
            const permission = existingPermissionId
                ? {
                      id: existingPermissionId,
                      name: PermissionType[key],
                      accessLevelId: accessLevelId,
                  }
                : (() => {
                      const composedPermission = composePermission(accessLevelId, key, idgen)
                      return {
                          id: composedPermission.id,
                          name: composedPermission.name,
                          accessLevelId: accessLevelId,
                      }
                  })()

            permissionsByKey[key] = permission
            return permission
        })

    return { permissions: permissions, permissionsByKey: permissionsByKey }
}

const buildPermissionActions = (
    permissionKeys: string[],
    newValue: RoleFormSubmittedPayload["newValue"],
    oldValue: RoleFormSubmittedPayload["oldValue"],
    permissionsByKey: Record<string, AccessLevelPermissionPayload>,
    accessLevelId: string
): Action[] => {
    return permissionKeys.reduce((acc, key) => {
        if (!!newValue[key]) {
            const existingPermissionId = oldValue?.[key]?.id
            if (existingPermissionId) {
                return acc
            }

            const permission = permissionsByKey[key]
            if (!permission) {
                return acc
            }

            acc.push(
                permissionCreated(
                    {
                        id: permission.id,
                        name: permission.name,
                        accessId: accessLevelId,
                    },
                    true
                )
            )
            return acc
        }

        const existingPermissionId = oldValue?.[key]?.id
        if (existingPermissionId) {
            acc.push(permissionDeleted(existingPermissionId, accessLevelId, true))
        }

        return acc
    }, [] as Action[])
}

const buildAccessLevelAction = (
    oldValue: RoleFormSubmittedPayload["oldValue"],
    accessLevelId: string,
    roleName: string
): Action => {
    return oldValue
        ? accessLevelUpdated(
              {
                  id: accessLevelId,
                  name: roleName,
                  permissions: [],
                  deleted: false,
              },
              true
          )
        : accessLevelCreated(
              {
                  id: accessLevelId,
                  name: roleName,
                  permissions: [],
                  deleted: false,
              },
              true
          )
}

export const getAccessLevelSavePayload = (
    roleFormPayload: RoleFormSubmittedPayload,
    idgen: IdGenerator
): {
    payload: AccessLevelSaveRequestedPayload
    actions: Action[]
} => {
    const oldVal = roleFormPayload.oldValue
    const newVal = roleFormPayload.newValue
    const accessLevelId = oldVal?.accessLevelId ?? idgen.next()
    const permissionKeys = getPermissionKeys(newVal)
    const { permissions, permissionsByKey } = buildPermissions(permissionKeys, newVal, oldVal, accessLevelId, idgen)
    const permissionActions = buildPermissionActions(permissionKeys, newVal, oldVal, permissionsByKey, accessLevelId)
    const accessAction = buildAccessLevelAction(oldVal, accessLevelId, newVal.roleName)

    return {
        payload: {
            accessLevel: {
                id: accessLevelId,
                name: newVal.roleName,
            },
            permissions: permissions,
        },
        actions: [accessAction, ...permissionActions],
    }
}
